/**
 * AdminZinePage
 * Owner-only view of the most-liked drawings for the printed Prompt-Brush zine.
 * Invite artists for permission; filter by invitation status; tag accepted drawings
 * to a specific zine issue; and download a high-res copy of accepted drawings.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Loader2, Check, Mail, X, Download, Package } from 'lucide-react';
import { toast } from 'sonner';
import {
  getTopLiked,
  sendZineInvite,
  setZineIssue,
  downloadZineImage,
  downloadZineZip,
  type TopLikedArtwork,
} from '@/services/supabase-storage';

const MONO = { fontFamily: 'FK Grotesk Mono, monospace' } as const;
const ISSUES = [1, 2, 3, 4];

type Status = 'none' | 'invited' | 'accepted' | 'declined';
const statusOf = (a: TopLikedArtwork): Status =>
  a.zineAccepted ? 'accepted' : a.zineDeclined ? 'declined' : a.zineSent ? 'invited' : 'none';

/** The reaction "mark" — a filled dot, sized/colored via className like a lucide icon. */
function Dot({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex items-center justify-center ${className}`} aria-hidden="true">
      <span className="block w-1/2 h-1/2 rounded-full bg-current" />
    </span>
  );
}

function fmtDate(raw?: string | null): string {
  if (!raw) return '';
  const d = new Date(String(raw).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(raw).slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function AdminZinePage() {
  const [rows, setRows] = useState<TopLikedArtwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [savingIssue, setSavingIssue] = useState<Record<string, boolean>>({});
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [zipping, setZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState<{ done: number; total: number } | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [issueFilter, setIssueFilter] = useState<'all' | 'unassigned' | number>('all');

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      setError(null);
      setRows(await getTopLiked(2000)); // every drawing with at least one mark
    } catch (e: any) {
      setError(e?.message || 'Could not load drawings.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { all: rows.length, none: 0, invited: 0, accepted: 0, declined: 0 };
    for (const a of rows) c[statusOf(a)]++;
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    let list = rows;
    if (statusFilter !== 'all') list = list.filter((a) => statusOf(a) === statusFilter);
    if (statusFilter === 'accepted' && issueFilter !== 'all') {
      list = list.filter((a) => (issueFilter === 'unassigned' ? !a.zineIssue : a.zineIssue === issueFilter));
    }
    return list;
  }, [rows, statusFilter, issueFilter]);

  const invite = useCallback(async (a: TopLikedArtwork) => {
    // Locked once the artist has answered; while merely "invited" you can re-send.
    if (a.zineAccepted || a.zineDeclined || sending[a.id]) return;
    if (!a.artistEmail) {
      toast.error('No email on file for this artist.');
      return;
    }
    const verb = a.zineSent ? 'Re-send the invite to' : 'Email';
    const ok = window.confirm(
      `${verb} ${a.artistName || 'this artist'} (${a.artistEmail}) to ask permission to print this drawing in the Prompt-Brush zine?`
    );
    if (!ok) return;
    setSending((s) => ({ ...s, [a.id]: true }));
    try {
      await sendZineInvite(a.id);
      setRows((prev) => prev.map((r) => (r.id === a.id ? { ...r, zineSent: true } : r)));
      toast.success(`Invite sent to ${a.artistName || a.artistEmail}.`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not send the invite.');
    } finally {
      setSending((s) => ({ ...s, [a.id]: false }));
    }
  }, [sending]);

  const chooseIssue = useCallback(async (a: TopLikedArtwork, issue: number) => {
    if (savingIssue[a.id]) return;
    const next = a.zineIssue === issue ? null : issue; // tap the current issue again to clear
    setSavingIssue((s) => ({ ...s, [a.id]: true }));
    // Optimistic; revert on failure.
    setRows((prev) => prev.map((r) => (r.id === a.id ? { ...r, zineIssue: next } : r)));
    try {
      await setZineIssue(a.id, next);
    } catch (e: any) {
      setRows((prev) => prev.map((r) => (r.id === a.id ? { ...r, zineIssue: a.zineIssue } : r)));
      toast.error(e?.message || 'Could not set the issue.');
    } finally {
      setSavingIssue((s) => ({ ...s, [a.id]: false }));
    }
  }, [savingIssue]);

  const download = useCallback(async (a: TopLikedArtwork) => {
    if (downloading[a.id]) return;
    setDownloading((s) => ({ ...s, [a.id]: true }));
    try {
      await downloadZineImage(a);
    } catch (e: any) {
      toast.error(e?.message || 'Download failed.');
    } finally {
      setDownloading((s) => ({ ...s, [a.id]: false }));
    }
  }, [downloading]);

  const exportZip = useCallback(async () => {
    if (zipping || visible.length === 0) return;
    const name =
      issueFilter === 'all'
        ? 'promptbrush-accepted'
        : issueFilter === 'unassigned'
        ? 'promptbrush-accepted-unassigned'
        : `promptbrush-zine-${issueFilter}`;
    setZipping(true);
    setZipProgress({ done: 0, total: visible.length });
    try {
      await downloadZineZip(visible, name, (done, total) => setZipProgress({ done, total }));
      toast.success(`Exported ${visible.length} drawing${visible.length === 1 ? '' : 's'}.`);
    } catch (e: any) {
      toast.error(e?.message || 'Export failed.');
    } finally {
      setZipping(false);
      setZipProgress(null);
    }
  }, [zipping, visible, issueFilter]);

  const filters: { key: 'all' | Status; label: string; n: number }[] = [
    { key: 'all', label: 'All', n: counts.all },
    { key: 'none', label: 'Uninvited', n: counts.none },
    { key: 'invited', label: 'Invited', n: counts.invited },
    { key: 'accepted', label: 'Accepted', n: counts.accepted },
    { key: 'declined', label: 'Declined', n: counts.declined },
  ];

  return (
    <div className="container mx-auto px-4 py-12 max-w-6xl">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-medium" style={MONO}>Zine</h1>
        <button
          onClick={() => load(true)}
          disabled={refreshing || loading}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          style={MONO}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <p className="text-muted-foreground/70 text-xs mb-5" style={MONO}>
        Every drawing with at least one mark. Invite an artist; when they answer, the card updates
        automatically. Tag accepted drawings to an issue and download a high-res copy for print.
      </p>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        {filters.map((f) => {
          const active = statusFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                active ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:text-foreground'
              }`}
              style={MONO}
            >
              {f.label} <span className="opacity-60">{f.n}</span>
            </button>
          );
        })}
      </div>

      {/* Issue filter + batch export — only relevant to accepted drawings */}
      {statusFilter === 'accepted' && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mr-1" style={MONO}>Issue</span>
            {(['all', ...ISSUES, 'unassigned'] as const).map((k) => {
              const active = issueFilter === k;
              const label = k === 'all' ? 'All' : k === 'unassigned' ? 'Unassigned' : `Zine ${k}`;
              return (
                <button
                  key={String(k)}
                  onClick={() => setIssueFilter(k as any)}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                    active ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                  }`}
                  style={MONO}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {visible.length > 0 && (
            <button
              onClick={exportZip}
              disabled={zipping}
              className="flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-60"
              style={MONO}
            >
              {zipping ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {zipProgress ? `${zipProgress.done}/${zipProgress.total}` : 'Zipping…'}
                </>
              ) : (
                <>
                  <Package className="w-3.5 h-3.5" />
                  Download {issueFilter === 'all' ? 'all accepted' : issueFilter === 'unassigned' ? 'unassigned' : `Zine ${issueFilter}`} ({visible.length}) as zip
                </>
              )}
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-3" />
          <p className="text-muted-foreground text-sm" style={MONO}>Loading drawings…</p>
        </div>
      ) : error ? (
        <div className="bg-card border border-border rounded-lg p-6 text-center">
          <p className="text-muted-foreground mb-3" style={MONO}>{error}</p>
          <button onClick={() => load(true)} className="text-sm text-foreground underline" style={MONO}>Try again</button>
        </div>
      ) : visible.length === 0 ? (
        <p className="text-muted-foreground text-sm" style={MONO}>Nothing here for this filter.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {visible.map((a) => {
            const isSending = !!sending[a.id];
            const noEmail = !a.artistEmail;
            const status = statusOf(a);
            const locked = status === 'accepted' || status === 'declined';
            return (
              <div key={a.id} className="bg-card border border-border rounded-lg overflow-hidden flex flex-col">
                <div className="relative aspect-[5/7] bg-background">
                  {a.imageUrl ? (
                    <img src={a.imageUrl} alt={a.promptText || 'Drawing'} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground/40 text-xs" style={MONO}>no image</div>
                  )}
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/70 text-white rounded-full px-2 py-0.5 text-xs" style={MONO}>
                    <Dot className="w-3 h-3" />
                    {a.heartCount}
                  </div>
                  {status === 'accepted' && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-green-600 text-white rounded-full px-2 py-0.5 text-[10px]" style={MONO}>
                      <Check className="w-3 h-3" /> {a.zineIssue ? `Zine ${a.zineIssue}` : 'In zine'}
                    </div>
                  )}
                  {status === 'declined' && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-neutral-500 text-white rounded-full px-2 py-0.5 text-[10px]" style={MONO}>
                      <X className="w-3 h-3" /> Declined
                    </div>
                  )}
                  {status === 'invited' && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-amber-500 text-white rounded-full px-2 py-0.5 text-[10px]" style={MONO}>
                      <Mail className="w-3 h-3" /> Invited
                    </div>
                  )}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <div className="text-muted-foreground/60 text-[10px]" style={MONO}>
                    {a.promptNumber ? `#${String(a.promptNumber).padStart(4, '0')}` : '—'}
                  </div>
                  {a.promptText && (
                    <div className="text-foreground text-xs mt-1 line-clamp-2" style={MONO}>{a.promptText}</div>
                  )}
                  {a.artistName && (
                    <div className="text-muted-foreground/70 text-[10px] mt-1 truncate" style={MONO}>by {a.artistName}</div>
                  )}
                  <div className="text-muted-foreground/50 text-[10px] truncate" style={MONO}>
                    {a.artistEmail || 'no email'}
                  </div>

                  {status === 'accepted' ? (
                    <div className="mt-2 flex flex-col gap-2">
                      <div className="text-green-600 text-[10px]" style={MONO}>
                        Accepted {fmtDate(a.acceptedAt)}
                      </div>
                      {/* Issue tagging */}
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-muted-foreground/60 mr-0.5" style={MONO}>Zine</span>
                        {ISSUES.map((n) => {
                          const on = a.zineIssue === n;
                          return (
                            <button
                              key={n}
                              onClick={() => chooseIssue(a, n)}
                              disabled={!!savingIssue[a.id]}
                              className={`w-6 h-6 rounded text-[11px] border transition-colors disabled:opacity-50 ${
                                on ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                              }`}
                              style={MONO}
                              aria-label={`Assign to zine ${n}`}
                            >
                              {n}
                            </button>
                          );
                        })}
                      </div>
                      {/* High-res download */}
                      <button
                        onClick={() => download(a)}
                        disabled={!!downloading[a.id]}
                        className="flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs bg-foreground text-background hover:opacity-90 transition-opacity disabled:opacity-60"
                        style={MONO}
                      >
                        {downloading[a.id] ? (
                          <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing…</>
                        ) : (
                          <><Download className="w-3.5 h-3.5" /> Download hi-res</>
                        )}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => invite(a)}
                      disabled={locked || isSending || noEmail}
                      className={`mt-3 flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs transition-colors disabled:opacity-60 ${
                        status === 'declined'
                          ? 'bg-neutral-500/10 text-neutral-500 cursor-default'
                          : status === 'invited'
                          ? 'border border-border text-foreground hover:bg-accent'
                          : 'bg-foreground text-background hover:opacity-90'
                      }`}
                      style={MONO}
                    >
                      {isSending ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                      ) : status === 'declined' ? (
                        <><X className="w-3.5 h-3.5" /> Declined</>
                      ) : status === 'invited' ? (
                        <><Mail className="w-3.5 h-3.5" /> Re-send invite</>
                      ) : noEmail ? (
                        'No email'
                      ) : (
                        <><Mail className="w-3.5 h-3.5" /> Invite to zine</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminZinePage;
